import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, Bank, ArrowsLeftRight } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function BankAccountsPage() {
  const { activeCompany, formatApiError } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [xferOpen, setXferOpen] = useState(false);
  const [xfer, setXfer] = useState({
    source_bank_account_id: "",
    destination_bank_account_id: "",
    amount: "",
    received_amount: "",
    date: new Date().toISOString().slice(0, 10),
    memo: "",
  });
  const [form, setForm] = useState({
    name: "",
    account_number: "",
    sort_code: "",
    opening_balance: "",
    opening_balance_date: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    if (!activeCompany) return;
    const { data } = await api.get(`/companies/${activeCompany.id}/bank-accounts`);
    setRows(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/companies/${activeCompany.id}/bank-accounts`, {
        name: form.name,
        currency: activeCompany.currency,
        account_number: form.account_number,
        sort_code: form.sort_code,
        opening_balance: parseFloat(form.opening_balance) || 0,
        opening_balance_date: form.opening_balance_date,
      });
      toast.success("Bank account created");
      setOpen(false);
      setForm({
        name: "",
        account_number: "",
        sort_code: "",
        opening_balance: "",
        opening_balance_date: new Date().toISOString().slice(0, 10),
      });
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!xfer.source_bank_account_id || !xfer.destination_bank_account_id) {
      return toast.error("Choose both source and destination accounts");
    }
    if (xfer.source_bank_account_id === xfer.destination_bank_account_id) {
      return toast.error("Source and destination must differ");
    }
    const src = rows.find((r) => r.id === xfer.source_bank_account_id);
    const dst = rows.find((r) => r.id === xfer.destination_bank_account_id);
    const crossCcy = src && dst && src.currency !== dst.currency;
    try {
      const payload = {
        source_bank_account_id: xfer.source_bank_account_id,
        destination_bank_account_id: xfer.destination_bank_account_id,
        amount: parseFloat(xfer.amount),
        date: xfer.date,
        memo: xfer.memo || undefined,
      };
      if (crossCcy) payload.received_amount = parseFloat(xfer.received_amount);
      const { data } = await api.post(
        `/companies/${activeCompany.id}/bank-accounts/transfer`,
        payload,
      );
      const fx = data.fx_difference !== 0
        ? ` · FX diff ${data.fx_difference.toFixed(2)}`
        : "";
      toast.success(`Transfer posted (Journal ${data.journal_id.slice(0, 8)})${fx}`);
      setXferOpen(false);
      setXfer({
        source_bank_account_id: "", destination_bank_account_id: "",
        amount: "", received_amount: "", date: new Date().toISOString().slice(0, 10), memo: "",
      });
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bank account?")) return;
    try {
      await api.delete(`/companies/${activeCompany.id}/bank-accounts/${id}`);
      toast.success("Deleted");
      load();
    } catch (e2) {      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  const totalBalance = rows.reduce((s, r) => s + (r.balance || 0), 0);

  return (
    <>
      <PageHeader
        eyebrow="Cash Management"
        title="Bank Accounts"
        description="Each bank account gets its own asset account in your chart of accounts. Use bank accounts when importing statements and matching transactions."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setXferOpen(true)}
              disabled={rows.length < 2}
              className="rounded-sm h-9 font-mono uppercase tracking-wider text-xs"
              data-testid="bank-transfer-btn"
              title={rows.length < 2 ? "Create at least two bank accounts first" : "Transfer between bank accounts"}
            >
              <ArrowsLeftRight size={14} className="mr-2" /> Transfer
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="new-bank-btn"
                  className="rounded-sm h-9 font-mono uppercase tracking-wider text-xs"
                >
                  <Plus size={14} className="mr-2" /> New Bank Account
                </Button>
              </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl">New Bank Account</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="bank-name"
                    className="rounded-sm font-mono"
                    placeholder="e.g. Chase Business Checking"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Account #</Label>
                    <Input
                      value={form.account_number}
                      onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                      className="rounded-sm font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Sort / Routing</Label>
                    <Input
                      value={form.sort_code}
                      onChange={(e) => setForm({ ...form, sort_code: e.target.value })}
                      className="rounded-sm font-mono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Opening Balance</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.opening_balance}
                      onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
                      data-testid="bank-opening-balance"
                      className="rounded-sm font-mono text-right"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">As of</Label>
                    <Input
                      type="date"
                      value={form.opening_balance_date}
                      onChange={(e) => setForm({ ...form, opening_balance_date: e.target.value })}
                      className="rounded-sm font-mono"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    data-testid="bank-submit"
                    className="rounded-sm font-mono uppercase tracking-wider text-xs"
                  >
                    Create
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
            </Dialog>
          </div>
        }
      />
      <PageBody>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          <div className="bg-card p-6">
            <div className="overline">Accounts</div>
            <div className="font-serif-display text-3xl num mt-2">{rows.length}</div>
          </div>
          <div className="bg-card p-6 md:col-span-2">
            <div className="overline">Combined Cash Balance</div>
            <div className="font-serif-display text-3xl num mt-2">
              {fmtMoney(totalBalance, activeCompany.currency)}
            </div>
          </div>
        </div>

        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <Th>Name</Th>
                <Th>Account #</Th>
                <Th>Sort / Routing</Th>
                <Th>Currency</Th>
                <Th className="text-right">Balance</Th>
                <Th className="text-right pr-4"></Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`bank-row-${r.id}`}
                  className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors duration-200"
                >
                  <Td className="font-mono flex items-center gap-2">
                    <Bank size={14} className="text-muted-foreground" />
                    {r.name}
                  </Td>
                  <Td className="num">{r.account_number || "—"}</Td>
                  <Td className="num">{r.sort_code || "—"}</Td>
                  <Td className="num">{r.currency}</Td>
                  <Td className="text-right num font-medium">
                    {fmtMoney(r.balance, r.currency)}
                  </Td>
                  <Td className="text-right pr-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(r.id)}
                      className="rounded-sm h-8 text-destructive"
                    >
                      <Trash size={14} />
                    </Button>
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">
                    <Bank size={20} className="inline mr-2 opacity-40" />
                    No bank accounts yet. Add one to start importing statements.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>

      <Dialog open={xferOpen} onOpenChange={setXferOpen}>
        <DialogContent className="rounded-sm" data-testid="bank-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Transfer between bank accounts</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitTransfer} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">From</Label>
                <Select
                  value={xfer.source_bank_account_id}
                  onValueChange={(v) => setXfer({ ...xfer, source_bank_account_id: v })}
                >
                  <SelectTrigger data-testid="bank-transfer-from" className="rounded-sm h-9 font-mono">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    {rows.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name} · {b.currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">To</Label>
                <Select
                  value={xfer.destination_bank_account_id}
                  onValueChange={(v) => setXfer({ ...xfer, destination_bank_account_id: v })}
                >
                  <SelectTrigger data-testid="bank-transfer-to" className="rounded-sm h-9 font-mono">
                    <SelectValue placeholder="Destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {rows
                      .filter((b) => b.id !== xfer.source_bank_account_id)
                      .map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name} · {b.currency}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(() => {
              const src = rows.find((r) => r.id === xfer.source_bank_account_id);
              const dst = rows.find((r) => r.id === xfer.destination_bank_account_id);
              const cross = src && dst && src.currency !== dst.currency;
              return (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-widest">
                        Amount sent {src ? `(${src.currency})` : ""}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={xfer.amount}
                        onChange={(e) => setXfer({ ...xfer, amount: e.target.value })}
                        required
                        data-testid="bank-transfer-amount"
                        className="rounded-sm font-mono text-right"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest">Date</Label>
                      <Input
                        type="date"
                        value={xfer.date}
                        onChange={(e) => setXfer({ ...xfer, date: e.target.value })}
                        required
                        className="rounded-sm font-mono"
                      />
                    </div>
                  </div>
                  {cross && (
                    <div className="p-3 border border-amber-400/40 bg-amber-50 rounded-sm">
                      <div className="text-xs text-amber-900 mb-2">
                        Cross-currency: {src.currency} → {dst.currency}. Enter the exact amount that
                        actually landed in the destination account (from your FX slip). Any residual
                        difference is auto-booked to FX Gain / (Loss).
                      </div>
                      <Label className="text-xs uppercase tracking-widest">
                        Amount received ({dst.currency})
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={xfer.received_amount}
                        onChange={(e) => setXfer({ ...xfer, received_amount: e.target.value })}
                        required
                        data-testid="bank-transfer-received"
                        className="rounded-sm font-mono text-right"
                      />
                    </div>
                  )}
                </>
              );
            })()}
            <div>
              <Label className="text-xs uppercase tracking-widest">Memo</Label>
              <Input
                value={xfer.memo}
                onChange={(e) => setXfer({ ...xfer, memo: e.target.value })}
                placeholder="Optional description"
                className="rounded-sm font-mono"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setXferOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="bank-transfer-submit">
                Post transfer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
